import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["postType", "marketplaceFields", "priceInput", "contextBanner", "bannerIcon", "bannerText", "bannerHook", "titleInput", "contentInput"]

  // Post type context data
  static postTypeData = {
    marketplace: {
      icon: "🛍️",
      banner: "Mô tả rõ ràng = Bán nhanh hơn",
      hook: "Ai đó đang tìm chính món đồ này",
      titlePlaceholder: "Ví dụ: Bán iPhone 13 Pro Max 128GB",
      contentPlaceholder: "Mô tả chi tiết sản phẩm, tình trạng, lý do bán, thời gian sử dụng...",
      colors: { bg: "bg-green-50", border: "border-green-200", text: "text-green-800", hook: "text-green-600" }
    },
    question: {
      icon: "❓",
      banner: "Hỏi gì cũng được! Cộng đồng sẵn sàng giúp bạn",
      hook: "Câu hỏi của bạn có thể là điều 10 người khác muốn hỏi",
      titlePlaceholder: "Ví dụ: Có ai biết chỗ làm visa gia hạn không?",
      contentPlaceholder: "Hỏi chi tiết để nhận câu trả lời chính xác nhất...",
      colors: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", hook: "text-blue-600" }
    },
    free_talk: {
      icon: "💬",
      banner: "Nói chuyện thoải mái, chia sẻ mọi thứ",
      hook: "Một dòng chia sẻ, một người bớt cô đơn",
      titlePlaceholder: "Ví dụ: Cuối tuần này ai rảnh không?",
      contentPlaceholder: "Chia sẻ suy nghĩ, cảm xúc, câu chuyện của bạn...",
      colors: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-800", hook: "text-purple-600" }
    },
    mentoring: {
      icon: "🎓",
      banner: "Kinh nghiệm của bạn có thể giúp người khác",
      hook: "Bạn từng là người mới. Giờ đến lượt bạn.",
      titlePlaceholder: "Ví dụ: Chia sẻ kinh nghiệm xin việc tại Hàn",
      contentPlaceholder: "Mô tả vấn đề cần tư vấn hoặc kinh nghiệm muốn chia sẻ...",
      colors: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", hook: "text-amber-600" }
    }
  }

  connect() {
    // If editing a marketplace post, ensure styles are correct
    const selectedType = this.postTypeTargets.find(input => input.checked)
    if (selectedType) {
      this.updatePostTypeStyles(selectedType)
    }
    // Always toggle fields based on current selection
    this.toggleMarketplaceFields()
  }

  postTypeChanged(event) {
    this.toggleMarketplaceFields()
    this.updatePostTypeStyles(event.target)
    this.updateContextBanner(event.target.value)
    this.updatePlaceholders(event.target.value)
  }

  updatePostTypeStyles(selectedRadio) {
    // Remove active styles from all labels
    this.postTypeTargets.forEach(radio => {
      const label = radio.closest('label')
      if (label) {
        label.classList.remove('border-blue-500', 'bg-blue-50')
      }
    })

    // Add active styles to selected label
    const selectedLabel = selectedRadio.closest('label')
    if (selectedLabel) {
      selectedLabel.classList.add('border-blue-500', 'bg-blue-50')
    }
  }

  toggleMarketplaceFields() {
    // More reliable way to get checked value
    const checked = this.element.querySelector("input[name='post[post_type]']:checked")
    const isMarketplace = checked && checked.value === "marketplace"
    
    // Use toggle for cleaner code
    this.marketplaceFieldsTarget.classList.toggle("hidden", !isMarketplace)
    
    // Disable/enable all form fields inside marketplace fields
    const inputs = this.marketplaceFieldsTarget.querySelectorAll("input, select, textarea")
    inputs.forEach(input => {
      input.disabled = !isMarketplace
    })
    
    // Set required attribute on price field
    if (this.hasPriceInputTarget) {
      this.priceInputTarget.required = isMarketplace
    }
  }

  formatPrice(event) {
    const input = event.target
    let value = input.value.replace(/[^\d]/g, '') // Remove non-digits
    
    if (value) {
      // Format with thousands separator
      value = parseInt(value).toLocaleString('ko-KR')
    }
    
    input.value = value
  }

  // Remove formatting before form submission
  prepareForSubmit(event) {
    if (this.hasPriceInputTarget) {
      const rawValue = this.priceInputTarget.value.replace(/[^\d]/g, '')
      this.priceInputTarget.value = rawValue
    }
  }

  updateContextBanner(postType) {
    const data = this.constructor.postTypeData[postType]
    if (!data || !this.hasContextBannerTarget) return

    // Update banner content
    if (this.hasBannerIconTarget) {
      this.bannerIconTarget.textContent = data.icon
    }
    if (this.hasBannerTextTarget) {
      this.bannerTextTarget.textContent = data.banner
      // Update text color
      this.bannerTextTarget.className = `font-medium ${data.colors.text}`
    }
    if (this.hasBannerHookTarget) {
      this.bannerHookTarget.textContent = data.hook
      // Update hook color
      this.bannerHookTarget.className = `text-sm mt-1 ${data.colors.hook}`
    }

    // Update banner colors
    const banner = this.contextBannerTarget
    // Remove all color classes
    banner.classList.remove(
      "bg-green-50", "bg-blue-50", "bg-purple-50", "bg-amber-50", "bg-gray-50",
      "border-green-200", "border-blue-200", "border-purple-200", "border-amber-200", "border-gray-200"
    )
    // Add new color classes
    banner.classList.add(data.colors.bg, data.colors.border)
  }

  updatePlaceholders(postType) {
    const data = this.constructor.postTypeData[postType]
    if (!data) return

    if (this.hasTitleInputTarget) {
      this.titleInputTarget.placeholder = data.titlePlaceholder
    }
    if (this.hasContentInputTarget) {
      this.contentInputTarget.placeholder = data.contentPlaceholder
    }
  }
}